import {
    MutationCache,
    QueryCache,
    QueryClient,
    QueryClientProvider as TanstackQueryClientProvider,
} from '@tanstack/react-query';
import { trackException } from '@this/app-shared/dist/tools/insights/track.js';
import { contract } from '@this/shared/dist/apis/serviceaddinapi/index.js';
import { config } from '@this/shared/dist/tools/config.js';
import { tsRestFetchApi } from '@ts-rest/core';
import { initTsrReactQuery } from '@ts-rest/react-query/v5';
import { useMemo } from 'react';
import { useToast } from '../hooks/toast.js';
import { useWidget } from './widget.js';

export function useTsRest() {
    const { getToken } = useWidget();

    return useMemo(
        () =>
            initTsrReactQuery(contract, {
                baseUrl: config.service.serviceAddin.baseUrl,
                jsonQuery: true,
                api: async (args) => {
                    const token = await getToken();

                    return tsRestFetchApi({
                        ...args,
                        headers: {
                            ...args.headers,
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                    });
                },
            }),
        [getToken]
    );
}

function useCreateQueryClient() {
    const toast = useToast();

    const queryClient = useMemo(() => {
        return new QueryClient({
            queryCache: new QueryCache({
                onError: (error) => {
                    trackException(error, true);
                    toast('Fehler', 'Fehler bei der Datenübermittlung', { intent: 'error' });
                },
            }),
            mutationCache: new MutationCache({
                onError: (error) => {
                    trackException(error, true);
                    toast('Fehler', 'Fehler bei der Datenübermittlung', { intent: 'error' });
                },
            }),
            defaultOptions: {
                queries: {
                    refetchOnWindowFocus: 'always',
                },
            },
        });
    }, [toast]);

    return queryClient;
}

export function QueryClientProvider({ children }: { children: React.ReactNode }) {
    const queryClient = useCreateQueryClient();
    const tsr = useTsRest();

    return (
        <TanstackQueryClientProvider client={queryClient}>
            <tsr.ReactQueryProvider>{children}</tsr.ReactQueryProvider>
        </TanstackQueryClientProvider>
    );
}
